import { Nullable } from "./utils";

export type TeamBillingError = "payment_failed" | "subscription_deleted" | "no_active_subscription";

export type TeamBillingDTO = {
  enabled: boolean;
  email: Nullable<string>;
  invoiceEmailEnabled: boolean;
  trialEndsAt: Nullable<Date>;
  billingError: Nullable<TeamBillingError>;
  apiCreditsSubscription: Nullable<{
    packageId: string;
    credits: number;
    priceMonthly: number;
    currentPeriodEndsAt: Date;
    cancelAtPeriodEnd: boolean;
  }>;
  apiCreditsBalance: Nullable<number>;
};

export type TeamBillingPortalDTO = {
  url: string;
};

/** A single field-level change computed by a dry-run Stripe sync. */
export type BillingSyncChange = {
  /** Stable field identifier (the Team column key), translated for display by the UI. */
  field: string;
  /** Current value in Shaarp, formatted for display (null = empty). */
  before: Nullable<string>;
  /** Value that would be applied, formatted for display (null = empty). */
  after: Nullable<string>;
};

/** Preview of the changes a Push/Pull Stripe sync would apply, without persisting. */
export type BillingSyncPreviewDTO = {
  direction: "push" | "pull";
  /** Changes that would be written to the Shaarp team record. */
  shaarpChanges: BillingSyncChange[];
  /** Changes that would be written to Stripe (empty for pull-only syncs). */
  stripeChanges: BillingSyncChange[];
  hasChanges: boolean;
};

export type TeamBillingPriceDTO = {
  id: string;
  productName: string;
  interval: "month" | "year";
  amount: number;
  licenceType: "professional" | null;
};

export type TeamBillingSetupIntentDTO = {
  clientSecret: string;
};

// --- License Stock Management ---

export type LicenseStockDTO = {
  purchased: number;
  assigned: number;
  available: number;
};

export type LicenseFrequencyDTO = {
  /**
   * @deprecated Always zero since #744. Kept for one release: a tab still on
   * the bundle loaded before it reads `basic.purchased` without a guard, and a
   * missing object blanks its billing page. Removed with the release that drops
   * the `purchased_basic_*` columns. Never read it.
   */
  basic: LicenseStockDTO;
  pro: LicenseStockDTO;
  /** @deprecated Always null since #744 — same reason and lifetime as `basic`. */
  scheduledBasic: Nullable<number>;
  /** Quantity scheduled to take effect at the next renewal (null = no scheduled change) */
  scheduledPro: Nullable<number>;
  scheduledChangeEffectiveAt: Nullable<Date>;
  subscriptionStatus: Nullable<string>;
  currentPeriodEndsAt: Nullable<Date>;
  promotionCode: Nullable<string>;
  percentOff: Nullable<number>;
  cancelAtPeriodEnd: boolean;
  /** Customer or subscription default; optional while older API instances roll out. */
  hasPaymentMethod?: boolean;
  /** Collection for this frequency, including a default on this subscription. */
  paidByInvoice?: boolean;
};

export type TeamLicenseInventoryDTO = {
  monthly: LicenseFrequencyDTO;
  annual: LicenseFrequencyDTO;
  defaultBillingFrequency: "monthly" | "annual";
  hasPaymentMethod: boolean;
  /**
   * Increases are invoiced at 14 days instead of charged: the team is exempted
   * from saving a payment method and holds none (#742).
   */
  paidByInvoice: boolean;
  /**
   * Free (admin-managed) plan: seats are admin-granted and monthly-only, with no
   * billing. The manager assigns seats but cannot buy, change or cancel them, and
   * no billing / frequency information is surfaced.
   */
  freePlan: boolean;
  prices: TeamBillingPriceDTO[];
};

/**
 * A Stripe invoice the team pays by bank transfer: a team with no saved payment
 * method is invoiced instead of charged (#742). Amounts are in major units.
 */
export type TeamInvoiceDTO = {
  amountDue: number;
  currency: string;
  dueAt: Nullable<Date>;
  /** Stripe's hosted invoice page, where it is paid. */
  hostedInvoiceUrl: Nullable<string>;
};

/**
 * The oldest open invoice that can block the team: sent for payment by
 * transfer and issued after the blocking cutoff. From the day after its due
 * date (Paris time) the whole team is blocked until it is paid.
 */
export type TeamUnpaidInvoiceDTO = TeamInvoiceDTO & {
  blocked: boolean;
};

/**
 * What a seat change returns: the inventory, plus the invoice it issued when
 * the team has no payment method to charge (null when it was charged, or when
 * nothing was billed now — a decrease, a trial).
 */
export type LicenseQuantitiesUpdateResultDTO = TeamLicenseInventoryDTO & {
  issuedInvoice: Nullable<TeamInvoiceDTO>;
};

export type UpdateLicenseQuantitiesDTO = {
  frequency: "monthly" | "annual";
  proQuantity: number;
  /** Accepted team users selected for scheduled unassignment when reducing. */
  usersToUnassign?: string[];
  /** Pending invitations selected for immediate cancellation when reducing —
   * pending invitations count against the assigned total and must be picked
   * alongside (or instead of) accepted users to satisfy the reduction. */
  invitationsToCancel?: string[];
};

/**
 * Why a team cancels, in the order the cancellation dialog lists them (fixed on
 * 2026-09-14). The order is deliberate: the first option of such a list is
 * over-picked, so the answer expected to dominate ("not used enough") is kept
 * off the first row instead of randomising the list.
 */
export const LICENSE_CANCELLATION_REASONS = [
  "missing_feature",
  "irrelevant_results",
  "missing_target_data",
  "not_used_enough",
  "price_not_justified",
  "switched_tool",
  "business_change",
  "other",
] as const;

export type LicenseCancellationReason = (typeof LICENSE_CANCELLATION_REASONS)[number];

/** Longest comment accepted with a cancellation; a longer one is refused, not cut. */
export const LICENSE_CANCELLATION_COMMENT_MAX_LENGTH = 500;

export type CancelLicenseSubscriptionDTO = {
  frequency: "monthly" | "annual";
  /** Manager confirmation that all assigned users will lose access at period end */
  confirmed: boolean;
  /** Optional reason picked in the dialog. */
  reason?: Nullable<LicenseCancellationReason>;
  /** Optional free text, at most {@link LICENSE_CANCELLATION_COMMENT_MAX_LENGTH} characters. */
  comment?: Nullable<string>;
};

export type LicensePreviewDTO = {
  immediateChargeExclTax: number;
  immediateChargeInclTax: number;
  immediateTax: number;
  immediateDiscountAmount: number;
  nextRenewalExclTax: number;
  currentRenewalExclTax: number;
  promotionCode: Nullable<string>;
  percentOff: Nullable<number>;
  currency: string;
  prorationStartDate: Nullable<Date>;
  prorationEndDate: Nullable<Date>;
  prorationDays: Nullable<number>;
  /** True if change is a decrease (will be scheduled, no immediate charge) */
  isScheduledChange: boolean;
  scheduledEffectiveAt: Nullable<Date>;
};

// --- Anthropic-inspired user license change workflow ---

export type UserLicenseChangePreviewDTO = {
  /** True if the change can be applied with a simple swap (stock available). */
  isSimpleSwap: boolean;
  /** True if a pool adjustment (purchase/release) is required. */
  requiresPoolAdjustment: boolean;
  poolBefore: {
    frequency: "monthly" | "annual";
    pro: number;
  };
  poolAfter: {
    frequency: "monthly" | "annual";
    pro: number;
  };
  totalSeatsBefore: number;
  totalSeatsAfter: number;
  prorationLines: Array<{
    label: string;
    quantityDelta: number;
  }>;
  immediateChargeExclTax: number;
  immediateChargeInclTax: number;
  immediateTax: number;
  immediateDiscountAmount: number;
  /** Null on a simple swap — the recurring cost is unchanged. */
  newRecurringExclTax: Nullable<number>;
  paymentMethodLast4: Nullable<string>;
  paymentMethodBrand: Nullable<string>;
  isScheduledChange: boolean;
  scheduledEffectiveAt: Nullable<Date>;
  /** Null on a simple swap — nothing is charged. */
  currency: Nullable<string>;
};

export type UserLicenseChangeConfirmDTO = {
  targetPriceId: string;
  /** Required true when requiresPoolAdjustment is true; ignored otherwise. */
  confirmed: boolean;
};

// --- Cross-frequency switch (monthly → annual only) ---

export type FrequencySwitchPreviewDTO = {
  fromFrequency: "monthly" | "annual";
  toFrequency: "monthly" | "annual";
  currentRecurringCost: number;
  newRecurringCost: number;
  estimatedYearlySavings: number;
  /** Catalog-price estimate of the first annual invoice, excl. tax and
   * pre-discount — the real invoice adds tax and applies any promotion. */
  estimatedImmediateChargeExclTax: number;
  currency: string;
};

export type ApplyFrequencySwitchDTO = {
  from: "monthly";
  to: "annual";
  confirmed: boolean;
};
